import React, { useState, useEffect } from 'react';
import { 
  Car, 
  MapPin, 
  Clock, 
  Euro, 
  Phone, 
  User,
  CheckCircle,
  XCircle,
  Play,
  Square,
  Navigation
} from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';

const TripManagement = ({ driverId }) => {
  const [trips, setTrips] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showTripModal, setShowTripModal] = useState(false);

  useEffect(() => {
    if (driverId) {
      fetchTrips();
      // Auto-refresh every 30 seconds
      const interval = setInterval(fetchTrips, 30000);
      return () => clearInterval(interval);
    }
  }, [driverId, activeFilter]);

  const fetchTrips = async () => {
    try {
      const params = new URLSearchParams();
      if (activeFilter !== 'all') params.append('status', activeFilter);
      if (activeFilter === 'today') {
        params.append('date', new Date().toISOString().split('T')[0]);
      }

      const response = await axiosInstance.get(`/trips/driver/${driverId}?${params}`);
      if (response.data.success) {
        setTrips(response.data.data.trips);
        setStatistics(response.data.data.statistics);
        setError(null);
      }
    } catch (err) {
      setError('Yolculuklar yüklenemedi');
      console.error('Error fetching trips:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateTripStatus = async (tripId, status, additionalData = {}) => {
    try {
      const response = await axiosInstance.put(`/trips/${tripId}/status`, {
        status,
        ...additionalData
      });
      
      if (response.data.success) {
        await fetchTrips(); // Refresh trips
        if (showTripModal) setShowTripModal(false);
      }
    } catch (err) {
      setError('Yolculuk durumu güncellenemedi');
      console.error('Error updating trip status:', err);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      requested: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Talep Edildi', icon: <Clock size={16} /> },
      accepted: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Kabul Edildi', icon: <CheckCircle size={16} /> },
      started: { bg: 'bg-green-100', text: 'text-green-800', label: 'Başladı', icon: <Play size={16} /> },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Tamamlandı', icon: <CheckCircle size={16} /> },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'İptal Edildi', icon: <XCircle size={16} /> }
    };
    const config = statusConfig[status] || statusConfig.requested;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.icon}
        <span className="ml-1">{config.label}</span>
      </span>
    );
  };

  const canUpdateStatus = (trip, newStatus) => {
    const validTransitions = {
      requested: ['accepted', 'cancelled'],
      accepted: ['started', 'cancelled'],
      started: ['completed', 'cancelled'],
      completed: [],
      cancelled: []
    };
    return validTransitions[trip.status]?.includes(newStatus);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-gray-100 h-24 rounded-lg"></div>
            ))}
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-100 h-32 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Car className="mr-3 text-blue-600" />
            Yolculuk Yönetimi
          </h1>
          <p className="text-gray-600 mt-1">
            Yolculuklarınızı takip edin ve yönetin
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button 
            onClick={fetchTrips}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Tekrar Dene
          </button>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Toplam Yolculuk</p>
              <p className="text-2xl font-bold">{statistics.total_trips || 0}</p>
            </div>
            <Car className="h-8 w-8 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Tamamlanan</p>
              <p className="text-2xl font-bold">{statistics.completed_trips || 0}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Bugün</p>
              <p className="text-2xl font-bold">{statistics.today_trips || 0}</p>
            </div>
            <Clock className="h-8 w-8 text-purple-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm">Toplam Kazanç</p>
              <p className="text-2xl font-bold">₺{statistics.total_earnings?.toFixed(2) || '0.00'}</p>
            </div>
            <Euro className="h-8 w-8 text-yellow-200" />
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-4 border-b border-gray-200">
        {[
          { key: 'all', label: 'Tümü' },
          { key: 'requested', label: 'Bekleyen' },
          { key: 'accepted', label: 'Kabul Edilen' },
          { key: 'started', label: 'Devam Eden' },
          { key: 'completed', label: 'Tamamlanan' },
          { key: 'today', label: 'Bugün' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveFilter(tab.key)}
            className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors ${
              activeFilter === tab.key
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Trips List */}
      <div className="space-y-4">
        {trips.length === 0 ? (
          <div className="text-center py-12">
            <Car size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-2">
              {activeFilter === 'all' ? 'Henüz yolculuk bulunmuyor' : `${activeFilter} durumunda yolculuk bulunmuyor`}
            </p>
          </div>
        ) : (
          trips.map((trip) => (
            <div
              key={trip.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 mr-3">
                      Yolculuk #{trip.id}
                    </h3>
                    {getStatusBadge(trip.status)}
                    {trip.company_name && (
                      <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {trip.company_name}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    {new Date(trip.requested_at).toLocaleString('tr-TR')}
                  </p>
                </div>

                <div className="flex space-x-2">
                  {canUpdateStatus(trip, 'accepted') && (
                    <button
                      onClick={() => updateTripStatus(trip.id, 'accepted')}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    >
                      Kabul Et
                    </button>
                  )}
                  {canUpdateStatus(trip, 'started') && (
                    <button
                      onClick={() => updateTripStatus(trip.id, 'started')}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      Başlat
                    </button>
                  )}
                  {canUpdateStatus(trip, 'completed') && (
                    <button
                      onClick={() => {
                        setSelectedTrip(trip);
                        setShowTripModal(true);
                      }}
                      className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700"
                    >
                      Tamamla
                    </button>
                  )}
                  {canUpdateStatus(trip, 'cancelled') && (
                    <button
                      onClick={() => updateTripStatus(trip.id, 'cancelled')}
                      className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                    >
                      İptal Et
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-start">
                    <MapPin size={16} className="text-green-500 mr-2 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Kalkış</p>
                      <p className="text-sm text-gray-600">{trip.pickup_address}</p>
                    </div>
                  </div>
                  
                  {trip.destination_address && (
                    <div className="flex items-start">
                      <Navigation size={16} className="text-red-500 mr-2 mt-1" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Varış</p>
                        <p className="text-sm text-gray-600">{trip.destination_address}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  {trip.customer_name && (
                    <div className="flex items-center">
                      <User size={16} className="text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Müşteri</p>
                        <p className="text-sm text-gray-600">{trip.customer_name}</p>
                      </div>
                    </div>
                  )}
                  
                  {trip.customer_phone && (
                    <div className="flex items-center">
                      <Phone size={16} className="text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Telefon</p>
                        <p className="text-sm text-gray-600">{trip.customer_phone}</p>
                      </div>
                    </div>
                  )}

                  {trip.fare_amount && (
                    <div className="flex items-center">
                      <Euro size={16} className="text-green-500 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Ücret</p>
                        <p className="text-sm text-green-600 font-semibold">
                          ₺{parseFloat(trip.fare_amount).toFixed(2)}
                          {trip.driver_earnings && (
                            <span className="text-xs text-gray-500 ml-1">
                              (Kazanç: ₺{parseFloat(trip.driver_earnings).toFixed(2)})
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {trip.notes && (
                <div className="mt-4 p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-700">
                    <strong>Notlar:</strong> {trip.notes}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Complete Trip Modal */}
      {showTripModal && selectedTrip && (
        <CompleteTripModal
          trip={selectedTrip}
          onComplete={(data) => updateTripStatus(selectedTrip.id, 'completed', data)}
          onCancel={() => setShowTripModal(false)}
        />
      )}
    </div>
  );
};

// Complete Trip Modal Component
const CompleteTripModal = ({ trip, onComplete, onCancel }) => {
  const [formData, setFormData] = useState({
    fare_amount: '',
    trip_distance: '',
    trip_duration: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.fare_amount) {
      alert('Ücret bilgisi gereklidir');
      return;
    }
    onComplete(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Yolculuğu Tamamla
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ücret (₺) *
              </label>
              <input
                type="number"
                step="0.01"
                required
                value={formData.fare_amount}
                onChange={(e) => setFormData({...formData, fare_amount: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mesafe (km)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.trip_distance}
                onChange={(e) => setFormData({...formData, trip_distance: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Süre (dakika)
              </label>
              <input
                type="number"
                value={formData.trip_duration}
                onChange={(e) => setFormData({...formData, trip_duration: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notlar
              </label>
              <textarea
                rows="3"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Yolculuk hakkında notlar..."
              />
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              İptal
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Tamamla
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TripManagement;
